// Path: ./src/app/components/FormKit/formControls.tsx
import React from "react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  TextFieldProps,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { messages } from "../../../locales/messages";
import {
  FieldCounter,
  FieldFooter,
  FieldLabel,
  FieldLabelRow,
  FieldRoot,
  RequiredMark,
  VisuallyHidden,
  token,
} from "./styled";

interface FieldProps {
  id?: string;
  label: string;
  required?: boolean;
  help?: React.ReactNode;
  error?: string;
  counter?: { value: number; max: number };
  tooltip?: string;
  children: React.ReactNode;
}

export const Field = ({
  id,
  label,
  required,
  help,
  error,
  counter,
  tooltip,
  children,
}: FieldProps) => {
  const { t } = useTranslation();
  const isOver = counter ? counter.value > counter.max : false;
  return (
    <FieldRoot>
      <FieldLabelRow>
        <FieldLabel {...(id ? { as: "label" as const, htmlFor: id } : {})}>
          {label}
          {required && (
            <RequiredMark title={t(messages.form_required_hint())}>
              *
              <VisuallyHidden>
                {t(messages.form_required_hint())}
              </VisuallyHidden>
            </RequiredMark>
          )}
        </FieldLabel>
        {tooltip && (
          <Tooltip title={tooltip} placement="top" arrow enterDelay={200}>
            <InfoOutlinedIcon
              tabIndex={0}
              aria-label={tooltip}
              sx={{
                fontSize: 15,
                color: token.color.borderStrong,
                cursor: "help",
                "&:hover, &:focus-visible": { color: token.color.accent },
              }}
            />
          </Tooltip>
        )}
      </FieldLabelRow>
      {children}
      <FieldFooter $error={Boolean(error)}>
        <span>{error || help || ""}</span>
        {counter && (
          <FieldCounter $over={isOver}>
            {counter.value}/{counter.max}
          </FieldCounter>
        )}
      </FieldFooter>
    </FieldRoot>
  );
};

export const controlSx = {
  "& .MuiOutlinedInput-root": {
    height: token.controlHeight,
    borderRadius: token.radius.sm,
    backgroundColor: token.color.surface,
    fontSize: 14,
    "& fieldset": { borderColor: token.color.border },
    "&:hover fieldset": { borderColor: token.color.borderStrong },
    "&.Mui-focused fieldset": {
      borderColor: token.color.accent,
      borderWidth: "1px",
    },
    "&.Mui-error fieldset": { borderColor: token.color.danger },
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 12px",
    color: token.color.text,
    height: "100%",
    boxSizing: "border-box",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: "unset !important",
  },
} as const;

export const TextControl = (props: TextFieldProps) => (
  <TextField
    {...props}
    fullWidth
    size="small"
    sx={{ ...controlSx, ...props.sx }}
  />
);

interface SearchControlProps {
  value?: string;
  placeholder?: string;
  onSearch: (value: string) => void;
  debounceMs?: number;
  width?: number | string;
  dataTestId?: string;
}

export const SearchControl = ({
  value = "",
  placeholder,
  onSearch,
  debounceMs = 400,
  width = 260,
  dataTestId,
}: SearchControlProps) => {
  const { t } = useTranslation();
  const [text, setText] = React.useState(value);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const committedRef = React.useRef(value);
  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;

  React.useEffect(() => {
    if (value !== committedRef.current) {
      committedRef.current = value;
      setText(value);
    }
  }, [value]);

  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const commit = (next: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = next.trim();
    committedRef.current = term;
    onSearchRef.current(term);
  };

  const handleChange = (next: string) => {
    setText(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(next), debounceMs);
  };

  const label = placeholder ?? t(messages.Search());

  return (
    <TextField
      size="small"
      value={text}
      placeholder={label}
      onChange={(event) => handleChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit(text);
        }
        if (event.key === "Escape" && text) {
          setText("");
          commit("");
        }
      }}
      inputProps={{ "aria-label": label, "data-testid": dataTestId }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0.5 }}>
            <SearchIcon sx={{ fontSize: 18, color: token.color.textMuted }} />
          </InputAdornment>
        ),
        endAdornment: text ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label={t(messages.Filter_clear())}
              onClick={() => {
                setText("");
                commit("");
              }}
              sx={{ p: 0.25, color: token.color.textMuted }}
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        width,
        maxWidth: "100%",
        ...controlSx,
        "& .MuiOutlinedInput-root": {
          ...controlSx["& .MuiOutlinedInput-root"],
          height: 36,
          paddingLeft: "10px",
          paddingRight: "8px",
        },
        "& .MuiOutlinedInput-input": {
          ...controlSx["& .MuiOutlinedInput-input"],
          padding: 0,
          fontSize: 13,
        },
      }}
    />
  );
};

interface SelectControlProps extends Omit<
  TextFieldProps,
  "onChange" | "select"
> {
  options: { label: string; value: string }[];
  onValueChange: (value: string) => void;
  placeholder?: string;
}

export const SelectControl = ({
  options,
  onValueChange,
  placeholder,
  value,
  ...rest
}: SelectControlProps) => (
  <TextField
    {...rest}
    select
    fullWidth
    size="small"
    value={value ?? ""}
    onChange={(event) => onValueChange(event.target.value)}
    sx={{ ...controlSx, ...rest.sx }}
    SelectProps={{
      displayEmpty: true,
      renderValue: (selected) => {
        const current = String(selected ?? "");
        if (!current) {
          return (
            <span style={{ color: token.color.textMuted }}>
              {placeholder ?? ""}
            </span>
          );
        }
        return (
          options.find((option) => option.value === current)?.label ?? current
        );
      },
    }}
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value} sx={{ fontSize: 14 }}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
);

export interface Option {
  label: string;
  value: string;
}

interface AutocompleteControlProps {
  id: string;
  options: Option[];
  selected: string[];
  onSelectedChange: (values: string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  loading?: boolean;
  onSearch?: (value: string) => void;
  dataTestId?: string;
}

export const AutocompleteControl = ({
  id,
  options,
  selected,
  onSelectedChange,
  multiple = true,
  placeholder,
  loading,
  onSearch,
  dataTestId,
}: AutocompleteControlProps) => {
  const selectedOptions = options.filter((option) =>
    selected.includes(option.value),
  );

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      size="small"
      options={options}
      value={multiple ? selectedOptions : (selectedOptions[0] ?? null)}
      loading={loading}
      isOptionEqualToValue={(option, picked) => option.value === picked.value}
      getOptionLabel={(option) => option.label}
      onChange={(_event, next) => {
        if (Array.isArray(next)) {
          onSelectedChange(next.map((option) => option.value));
        } else {
          onSelectedChange(next ? [next.value] : []);
        }
      }}
      onInputChange={(_event, input, reason) => {
        if (reason === "input") onSearch?.(input);
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => (
          <Chip
            {...getTagProps({ index })}
            key={tag.value}
            label={tag.label}
            size="small"
            sx={{
              height: 22,
              fontSize: 12,
              borderRadius: token.radius.sm,
              backgroundColor: token.color.accentSoft,
              color: "#7a5230",
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedOptions.length ? undefined : placeholder}
          inputProps={{ ...params.inputProps, "data-testid": dataTestId }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} color="inherit" />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            ...controlSx,
            "& .MuiOutlinedInput-root": {
              ...controlSx["& .MuiOutlinedInput-root"],
              height: "auto",
              minHeight: token.controlHeight,
              paddingTop: "3px",
              paddingBottom: "3px",
            },
            "& .MuiOutlinedInput-input": {
              ...controlSx["& .MuiOutlinedInput-input"],
              height: "auto",
              padding: "4px 8px !important",
            },
          }}
        />
      )}
    />
  );
};

interface DateControlProps {
  id: string;
  value?: Date;
  onValueChange: (value?: Date) => void;
  dataTestId?: string;
}

export const DateControl = ({
  id,
  value,
  onValueChange,
  dataTestId,
}: DateControlProps) => (
  <DatePicker
    value={value ? dayjs(value) : null}
    onChange={(next) => onValueChange(next ? next.toDate() : undefined)}
    slotProps={{
      textField: {
        id,
        fullWidth: true,
        size: "small",
        placeholder: "MM/DD/YYYY",
        inputProps: { "data-testid": dataTestId },
        sx: controlSx,
      },
      actionBar: { actions: ["clear", "today", "accept"] },
    }}
  />
);
